'use client';

/**
 * MediaPicker selection previews.
 *
 * The presentational pieces the MediaPicker renders for an existing
 * selection — the multi-select thumbnail row (`SelectedThumbRow`) and the
 * single-select inline card (`SelectedInlineCard`) — plus the
 * `libraryItemToMediaFile` adapter both share with the picker's library grid.
 * Extracted from media-picker.tsx to keep that file within the code-size
 * budget; behaviour is unchanged.
 */
import type React from 'react';
import { X } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import { type MediaFile } from '../hooks/use-media-upload';
import type { MediaPickerLibraryItem } from './media-picker';
import { MediaThumb } from './media-thumb';

/**
 * Translates a `MediaPickerLibraryItem` into the `MediaFile` shape used by
 * `MediaThumb` so we can re-use the existing tile component.
 */
export function libraryItemToMediaFile(
  item: MediaPickerLibraryItem,
): MediaFile {
  // DancerMediaService.getMediaByUser already replaces `storage_path` with a
  // signed URL — that's still safe to pass to MediaThumb's loader chain.
  const renderUrl = item.signed_url ?? item.storage_path ?? null;
  return {
    id: item.id,
    storagePath: renderUrl,
    signedUrl: item.signed_url ?? renderUrl ?? undefined,
    fileName: item.title ?? undefined,
    mimeType: item.mime_type ?? undefined,
    fileSize: item.file_size_bytes ?? undefined,
    title: item.title ?? undefined,
    // Thread the video residency signals so MediaThumb/MediaLightbox route a
    // migrated video to its live source (Mux HLS / R2 CDN / external) instead of
    // the dead Supabase-Storage `signedUrl` — the confirmed apply-preview 400.
    externalUrl: item.external_url,
    muxPlaybackId: item.mux_playback_id,
    muxStatus: item.mux_status,
    r2Key: item.r2_key,
  };
}

/**
 * Translates a library row into the `MediaFile` an application answer stores,
 * the same shape an upload produces. The stored `storagePath` is the durable
 * key (`storage_key`), never the signed URL `storage_path` may carry, because
 * read surfaces re-sign from the key. The short-lived `signedUrl` rides along
 * for the first render only.
 */
export function libraryItemToStoredMediaFile(
  item: MediaPickerLibraryItem,
): MediaFile {
  const rendered = libraryItemToMediaFile(item);
  return {
    ...rendered,
    storagePath: item.storage_key ?? item.storage_path ?? null,
  };
}

interface SelectedThumbRowProps {
  items: MediaPickerLibraryItem[];
  removeAria: string;
  disabled?: boolean;
  onRemove: (id: string) => void;
  className?: string;
}

/**
 * Multi-select preview: a wrap row of thumbnails with an X button on each,
 * rendered above the picker's trigger button. The X clears the selection
 * for that id only — it does NOT delete the underlying dancer_media row
 * (deletion lives in the dialog's library tile menu, gated by getUsage).
 */
export function SelectedThumbRow({
  items,
  removeAria,
  disabled,
  onRemove,
  className,
}: SelectedThumbRowProps) {
  return (
    <ul className={cn('flex flex-wrap gap-2', className)}>
      {items.map((item) => (
        <li key={item.id} className="relative size-20">
          <MediaThumb file={libraryItemToMediaFile(item)} variant="grid" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onRemove(item.id)}
            disabled={disabled}
            aria-label={removeAria}
            className={cn(
              'hover:bg-destructive absolute -top-1.5 -right-1.5 z-10 flex size-6 items-center justify-center rounded-full bg-black/75 text-white shadow ring-1 ring-white/40 backdrop-blur-sm transition-colors',
              disabled && 'cursor-not-allowed opacity-50 hover:bg-black/75',
            )}
          >
            <X className="size-3.5" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

interface SelectedInlineCardProps {
  item: MediaPickerLibraryItem;
  replaceLabel: string;
  removeAria: string;
  disabled?: boolean;
  extras?: React.ReactNode;
  onReplace: () => void;
  onRemove: () => void;
}

/**
 * Single-select preview: a card with the chosen thumbnail, a Replace
 * button (re-opens the picker) and a Remove button. Replaces the picker
 * trigger entirely so the UI never says "Pick a headshot or upload" while
 * a headshot is already picked.
 */
export function SelectedInlineCard({
  item,
  replaceLabel,
  removeAria,
  disabled,
  extras,
  onReplace,
  onRemove,
}: SelectedInlineCardProps) {
  return (
    <div className="border-border/60 bg-muted/20 flex items-center gap-3 rounded-md border p-2">
      <div className="size-20 shrink-0">
        <MediaThumb file={libraryItemToMediaFile(item)} variant="grid" />
      </div>
      <div className="min-w-0 flex-1">
        {item.title && (
          <p className="truncate text-sm font-medium">{item.title}</p>
        )}
        {extras}
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onReplace}
          disabled={disabled}
        >
          {replaceLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeAria}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}
